/**
 * @file AvitoListingDetailPage.tsx
 * @description Детали одного Avito-листинга (phase14.0).
 *
 * Показывает:
 *  - все поля метаданных (Avito-статус / цена / адрес / категория Avito);
 *  - компоненты внутри карточки (товары набора);
 *  - ссылку на оригинал на Avito;
 *  - ссылку на Product в обычном admin-каталоге (для просмотра sku-карточки).
 */

import { ArrowLeft, ExternalLink, Loader2, Package } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router";

import { Alert } from "../../../components/ui/alert";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { EditorialCard } from "../../../components/ui/EditorialCard";
import { ApiError } from "../../../lib/api-client";
import {
  formatPennyToRub,
  getAvitoStatusLabel,
  shopAvito,
  type AvitoListingDetailDto,
} from "../../../lib/api-shop-avito";

export function AvitoListingDetailPage() {
  const { productId, slug } = useParams<{ productId: string; slug: string }>();
  const [data, setData] = useState<AvitoListingDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!productId) return;
    setLoading(true);
    setError(null);
    try {
      setData(await shopAvito.listingDetail(productId));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось загрузить листинг");
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return <Loader2 className="size-5 animate-spin mx-auto my-16 text-on-surface-variant" />;
  }
  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }
  if (!data) {
    return <Alert severity="warning">Листинг не найден.</Alert>;
  }

  const statusLabel = getAvitoStatusLabel(data.avitoStatus);
  const mainImage = data.images?.listing ?? null;
  const galleryImages = data.images?.list ?? [];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <Button asChild variant="ghost" size="sm">
          <Link to={`/shop/${slug}/avito/listings`}>
            <ArrowLeft className="size-4" />
            Назад к листингам
          </Link>
        </Button>
      </div>

      {/* Шапка: название + бейдж + цена */}
      <EditorialCard>
        <EditorialCard.Body className="space-y-3">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <h2 className="text-lg font-semibold text-on-surface">{data.displayName}</h2>
            <Badge
              variant={
                statusLabel.tone === "danger"
                  ? "destructive"
                  : statusLabel.tone === "warn"
                    ? "warning"
                    : "default"
              }
            >
              {statusLabel.label}
            </Badge>
          </div>
          <div className="text-2xl font-semibold text-on-surface">
            {formatPennyToRub(data.pricePenny)}
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button asChild variant="outline" size="sm">
              <a href={data.url} target="_blank" rel="noreferrer noopener">
                <ExternalLink className="size-4" />
                Открыть на Avito
              </a>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to={`/shop/${slug}/catalog/products/${data.productId}`}>
                <Package className="size-4" />
                Карточка товара в каталоге
              </Link>
            </Button>
          </div>
        </EditorialCard.Body>
      </EditorialCard>

      {/* Главное фото + галерея */}
      <EditorialCard>
        <EditorialCard.Header title="Фотографии" />
        <EditorialCard.Body className="space-y-3">
          {mainImage ? (
            <>
              <img
                src={mainImage}
                alt={data.displayName}
                className="w-full max-w-xl mx-auto rounded-md border border-outline-variant"
              />
              {galleryImages.length > 1 && (
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                  {galleryImages.map((url, i) => (
                    <img
                      key={`${i}-${url}`}
                      src={url}
                      alt={`Фото ${i + 1}`}
                      className="aspect-square w-full object-cover rounded border border-outline-variant"
                    />
                  ))}
                </div>
              )}
            </>
          ) : (
            <Alert severity="info">
              <p className="font-medium">Avito API не возвращает фотографии в основном эндпоинте.</p>
              <p className="text-xs text-on-surface-variant mt-1">
                URL фотографий отдаются только через autoload-эндпоинт (требует подключённого XML-фида автозагрузки на Avito) или при индивидуальном запросе на специальный endpoint. Чтобы посмотреть фото — нажмите «Открыть на Avito».
              </p>
            </Alert>
          )}
        </EditorialCard.Body>
      </EditorialCard>

      {/* Описание */}
      <EditorialCard>
        <EditorialCard.Header title="Описание (с Avito)" />
        <EditorialCard.Body>
          {data.description ? (
            <p className="text-sm text-on-surface whitespace-pre-line">{data.description}</p>
          ) : (
            <Alert severity="info">
              <p className="font-medium">Avito API не возвращает описание в основном эндпоинте.</p>
              <p className="text-xs text-on-surface-variant mt-1">
                Эндпоинт <code className="bg-surface-low px-1 rounded">/core/v1/items</code> отдаёт
                только название, цену, статус, адрес и категорию. Описание и фото — через
                autoload-эндпоинт (нужен XML-фид) или специальные endpoint'ы, поддержка которых
                запланирована в phase14.0.1. Сейчас откройте оригинал на Avito.
              </p>
            </Alert>
          )}
        </EditorialCard.Body>
      </EditorialCard>

      {/* Компоненты карточки */}
      <EditorialCard>
        <EditorialCard.Header
          title={`Товары внутри карточки (${data.components.length})`}
          subtitle="Компоненты, распарсенные из описания листинга. Это не отдельные SKU — это аннотация состава Avito-объявления."
        />
        <EditorialCard.Body className="p-0">
          {data.components.length === 0 ? (
            <div className="p-4 text-sm text-on-surface-variant text-center">
              Компоненты не распознаны автоматически. Можно добавить вручную при будущей правке
              описания на Avito.
            </div>
          ) : (
            <ul className="divide-y divide-outline-variant">
              {data.components.map((c) => (
                <li key={c.id} className="px-4 py-3 flex items-baseline justify-between gap-2">
                  <div>
                    <span className="text-on-surface-variant text-xs mr-2">#{c.position}</span>
                    <span className="font-medium text-on-surface">{c.name}</span>
                  </div>
                  <div className="text-sm text-on-surface-variant">
                    {c.qty ? `${c.qty} × ` : ""}
                    {c.unit ?? "—"}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </EditorialCard.Body>
      </EditorialCard>

      {/* Технические поля */}
      <EditorialCard>
        <EditorialCard.Header title="Технические данные" />
        <EditorialCard.Body className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <Field label="Avito ID" value={data.avitoItemId} />
          <Field label="Avito-статус" value={data.avitoStatus} />
          <Field
            label="Категория Avito"
            value={data.avitoCategory ? `${data.avitoCategory.name} (id ${data.avitoCategory.id})` : "—"}
          />
          <Field label="Адрес" value={data.address ?? "—"} />
          <Field label="Slug продукта" value={data.productSlug} />
          <Field label="Статус продукта" value={data.productStatus} />
          <Field label="Первое появление" value={new Date(data.firstSeenAt).toLocaleString("ru-RU")} />
          <Field label="Последняя синхра" value={new Date(data.lastSyncedAt).toLocaleString("ru-RU")} />
          {data.removedAt && (
            <Field
              label="Снят с публикации"
              value={new Date(data.removedAt).toLocaleString("ru-RU")}
            />
          )}
        </EditorialCard.Body>
      </EditorialCard>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-on-surface-variant">{label}</div>
      <div className="font-medium text-on-surface">{value}</div>
    </div>
  );
}
